import { useTranslation } from "react-i18next";
import { Box, Step, StepConnector, StepContent, StepLabel, Stepper, styled, Typography } from "@mui/material";

import { ActiveStepIcon, CompletedStepIcon, DefaultStepIcon } from "../assets/icons/go-hire-page-icons";

const CreateJobStepper = ({ currentStep = "job-details" }: { currentStep?: string }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "createJobPage.createJobStepper" });

  const CustomStepConnector = styled(StepConnector)(() => styles.stepConnector);

  const getStepIndex = (step: string) => {
    switch (step) {
      case "job-details":
        return 0;
      case "hiring-journey":
        return 1;
      case "job-boost":
        return 2;
      case "review-and-publish":
        return 3;
      default:
        return 0;
    }
  };

  const activeStepIndex = getStepIndex(currentStep);

  const getStepStatus = (index: number) => {
    if (index < activeStepIndex) return "completed";
    if (index === activeStepIndex) return "active";
    return "inactive";
  };

  const steps = [
    {
      title: t("jobDetails"),
      content: t("pleaseProvideYourDetailsAboutJob"),
      status: getStepStatus(0),
      isPro: false,
      step: "job-details",
    },
    {
      title: t("hiringJourney"),
      content: t("buildManageAndTrackHiringStages"),
      status: getStepStatus(1),
      isPro: true,
      step: "hiring-journey",
    },
    {
      title: t("jobBoost"),
      content: t("targetPromoteAndTrackPerformance"),
      status: getStepStatus(2),
      isPro: true,
      step: "job-boost",
    },
    {
      title: t("reviewAndPublishJob"),
      content: t("reviewDetailsSetPreferencesAndPublish"),
      status: getStepStatus(3),
      isPro: false,
      step: "review-and-publish",
    },
  ];

  return (
    <Box sx={styles.stepperWrapper}>
      <Box sx={styles.stepperContainer}>
        <Stepper
          sx={{ height: "fit-content" }}
          activeStep={activeStepIndex}
          orientation="vertical"
          connector={<CustomStepConnector />}
        >
          {steps.map((step, index) => (
            <CreateJobStep key={index} step={step} />
          ))}
        </Stepper>
      </Box>{" "}
    </Box>
  );
};

export default CreateJobStepper;

const CreateJobStep = ({
  step: { status, title, content, isPro },
}: {
  step: { status: string; title: string; content: string; isPro: boolean; step: string };
}) => {
  const { t } = useTranslation("goHire", { keyPrefix: "createJobPage.createJobStepper" });
  const StepIcon = () => {
    if (status === "completed") return <CompletedStepIcon />;
    if (status === "active") return <ActiveStepIcon />;
    return <DefaultStepIcon />;
  };

  return (
    <Step expanded={true} completed={true} sx={styles.step}>
      <StepLabel StepIconComponent={StepIcon}>
        <Box sx={styles.stepLabel}>
          <Typography sx={styles.stepTitle}>{title}</Typography>
          {isPro && (
            <Typography component="span" sx={styles.proLabel}>
              {t("pro")}
            </Typography>
          )}
        </Box>
      </StepLabel>
      <StepContent sx={styles.stepContent}>
        <Typography sx={styles.stepDescription}>{content}</Typography>
      </StepContent>
    </Step>
  );
};
const styles = {
  stepperWrapper: {
    minHeight: "100%",
    borderRight: "1px solid var(--Gray---100, #E7E7E7)",
  },
  stepperContainer: {
    flexShrink: 0,
    height: "100%",
    width: "310px",
    padding: "32px 24px 32px 0",
  },
  stepConnector: {
    "&.MuiStepConnector-root": {
      marginLeft: 15,
      display: "none",
      "&:not(:first-of-type)": {
        display: "block",
      },
    },
    "&.Mui-active, &.Mui-completed": {
      "& .MuiStepConnector-line": {
        borderColor: "var(--Green---100, #008B07)",
        borderLeftWidth: "2px",
      },
    },
    "& .MuiStepConnector-line": {
      borderColor: "var(--Gray-200, #EAECF0)",
      minHeight: "32px",
      borderLeftWidth: "2px",
    },
  },
  stepLabel: {
    display: "flex",
    gap: "8px",
  },
  stepTitle: {
    fontFamily: "Montserrat",
    color: "var(--Dark---800, #414141)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
  proLabel: {
    borderRadius: "6px",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    background: "var(--Base-White, #FFF)",
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    padding: "2px 6px",
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  stepContent: {
    "&.MuiStepContent-root": {
      marginLeft: "15px",
      borderLeft: "2px solid var(--Brand-600, #008B07)",

      "&:last-of-type": {},
    },
  },
  stepDescription: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  step: {
    cursor: "default",
    "&:last-of-type": {
      "& .MuiStepContent-root": {
        borderLeft: "none",
      },
    },
  },
};
